import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

/**
 * 可搜索多选下拉：已选项在输入框左侧外部；嵌套搜索框无边框。
 * options: [{ value, label, disabled? }]
 * value: string[]
 */
export default function SearchableMultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = '',
  disabled = false,
  emptyText = '',
  className = '',
}) {
  const { t } = useTranslation('workbench');
  const resolvedPlaceholder = placeholder || t('productCopy.searchDefault');
  const resolvedEmpty = emptyText || t('productCopy.emptyOptions');
  const rootRef = useRef(null);
  const controlRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [panelStyle, setPanelStyle] = useState({});
  const selected = useMemo(
    () => (Array.isArray(value) ? value.map(String) : []),
    [value],
  );

  const selectedOptions = useMemo(
    () => options.filter((opt) => selected.includes(String(opt.value))),
    [options, selected],
  );

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => String(opt.label || '').toLowerCase().includes(q));
  }, [options, keyword]);

  const updatePanelPosition = () => {
    const el = controlRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const maxHeight = Math.min(260, Math.max(120, window.innerHeight - rect.bottom - 12));
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 240),
      maxHeight,
      overflowX: 'hidden',
      overflowY: 'auto',
      zIndex: 13000,
    });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePanelPosition();
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, filtered.length, selected.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inRoot && !inPanel) {
        setOpen(false);
        setKeyword('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (nextValue) => {
    const id = String(nextValue);
    const set = new Set(selected);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange?.([...set]);
  };

  const removeOne = (nextValue) => {
    onChange?.(selected.filter((item) => item !== String(nextValue)));
  };

  const panel = open && !disabled ? createPortal(
    <div
      ref={panelRef}
      className="tpo-search-select__panel tpo-search-select__panel--portal"
      style={panelStyle}
    >
      {filtered.length === 0 ? (
        <div className="tpo-search-select__empty">{resolvedEmpty}</div>
      ) : filtered.map((opt) => {
        const active = selected.includes(String(opt.value));
        return (
          <button
            key={String(opt.value)}
            type="button"
            className={`tpo-search-select__option${active ? ' is-on' : ''}${opt.disabled ? ' is-disabled' : ''}`}
            disabled={!!opt.disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (opt.disabled) return;
              toggle(opt.value);
            }}
          >
            <input type="checkbox" readOnly checked={active} tabIndex={-1} />
            <span>{opt.label}</span>
          </button>
        );
      })}
    </div>,
    document.body,
  ) : null;

  return (
    <div
      className={`tpo-search-select tpo-search-select--multi${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}${className ? ` ${className}` : ''}`}
      ref={rootRef}
    >
      <div className="tpo-search-select__row">
        {selectedOptions.length > 0 ? (
          <div className="tpo-search-select__tags">
            {selectedOptions.map((opt) => (
              <span key={String(opt.value)} className="tpo-search-select__chip">
                <em>{opt.label}</em>
                <button
                  type="button"
                  aria-label={t('productCopy.remove')}
                  disabled={disabled}
                  onClick={() => removeOne(opt.value)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : null}

        <div
          className="tpo-search-select__control tpo-search-select__control--multi"
          ref={controlRef}
          onClick={() => {
            if (!disabled) setOpen(true);
          }}
        >
          <input
            className="tpo-search-select__inline-input"
            value={keyword}
            disabled={disabled}
            placeholder={resolvedPlaceholder}
            onFocus={() => {
              if (!disabled) setOpen(true);
            }}
            onChange={(e) => {
              setKeyword(e.target.value);
              if (!disabled) setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setOpen(false);
                setKeyword('');
              }
              if (e.key === 'Backspace' && !keyword && selected.length) {
                onChange?.(selected.slice(0, -1));
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <span className="tpo-search-select__caret" aria-hidden>▾</span>
        </div>
      </div>
      {panel}
    </div>
  );
}
